import { useState } from 'react'
import {
  CurecallButton,
  CurecallBadge,
  CurecallBanner,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { TwoFactorService } from 'src/services/two-factor.service'
import TwoFactorSetupTotp from './two-factor-setup-totp.component'
import TwoFactorSetupSms from './two-factor-setup-sms.component'
import TwoFactorDisableModal from './two-factor-disable-modal.component'
import TwoFactorRegenerateCodesModal from './two-factor-regenerate-codes-modal.component'

const TWO_FACTOR_STATUS_KEY = '2fa-status'
const LOW_BACKUP_CODES_THRESHOLD = 2

export default function TwoFactorSettings() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [showTotpSetup, setShowTotpSetup] = useState(false)
  const [showSmsSetup, setShowSmsSetup] = useState(false)
  const [showDisable, setShowDisable] = useState(false)
  const [showRegenerate, setShowRegenerate] = useState(false)

  const { data: status, isLoading } = useQuery({
    queryKey: [TWO_FACTOR_STATUS_KEY],
    queryFn: TwoFactorService.getStatus,
  })

  const invalidateStatus = () => {
    queryClient.invalidateQueries({ queryKey: [TWO_FACTOR_STATUS_KEY] })
  }

  if (isLoading) {
    return null
  }

  return (
    <div className="c-border c-border-grey-300 c-rounded-md c-p-3 c-w-full">
      <div className="c-flex c-items-center c-justify-between c-mb-4">
        <h4>{t('pages.auth.account.two-factor.title')}</h4>
        {status?.isEnabled && (
          <CurecallBadge color={ECurecallBadgeColor.GREEN}>
            {status.method === 'totp'
              ? t('pages.auth.account.two-factor.enabled-totp')
              : t('pages.auth.account.two-factor.enabled-sms')}
          </CurecallBadge>
        )}
      </div>

      {status?.isEnabled &&
        status.backupCodesRemaining !== null &&
        status.backupCodesRemaining <= LOW_BACKUP_CODES_THRESHOLD && (
          <CurecallBanner className="c-mb-4">
            {t('pages.auth.account.two-factor.low-backup-codes', {
              count: status.backupCodesRemaining,
            })}
          </CurecallBanner>
        )}

      {!status?.isEnabled ? (
        <div className="c-flex c-flex-col c-gap-4">
          <p className="c-text-sm c-text-gray-600">{t('pages.auth.account.two-factor.setup-description')}</p>
          <div className="c-flex c-gap-3">
            <CurecallButton onClick={() => setShowTotpSetup(true)}>
              {t('pages.auth.account.two-factor.setup-totp')}
            </CurecallButton>
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              onClick={() => setShowSmsSetup(true)}
            >
              {t('pages.auth.account.two-factor.setup-sms')}
            </CurecallButton>
          </div>
        </div>
      ) : (
        <div className="c-flex c-flex-col c-gap-4">
          <p className="c-text-sm c-text-gray-600">
            {t('pages.auth.account.two-factor.enabled-description', {
              method:
                status.method === 'totp'
                  ? t('pages.auth.account.two-factor.method-totp')
                  : t('pages.auth.account.two-factor.method-sms'),
            })}
          </p>
          {status.backupCodesRemaining !== null && (
            <p className="c-text-sm c-text-gray-500">
              {t('pages.auth.account.two-factor.backup-codes-remaining', {
                count: status.backupCodesRemaining,
              })}
            </p>
          )}
          <div className="c-flex c-gap-3">
            <CurecallButton
              appearance={ECurecallButtonAppearance.SECONDARY}
              onClick={() => setShowRegenerate(true)}
            >
              {t('pages.auth.account.two-factor.regenerate-backup-codes')}
            </CurecallButton>
            <CurecallButton
              appearance={ECurecallButtonAppearance.CANCEL}
              onClick={() => setShowDisable(true)}
            >
              {t('pages.auth.account.two-factor.disable')}
            </CurecallButton>
          </div>
        </div>
      )}

      <TwoFactorSetupTotp
        isOpen={showTotpSetup}
        onClose={() => setShowTotpSetup(false)}
        onSetupComplete={invalidateStatus}
      />
      <TwoFactorSetupSms
        isOpen={showSmsSetup}
        onClose={() => setShowSmsSetup(false)}
        onSetupComplete={invalidateStatus}
      />
      <TwoFactorDisableModal
        isOpen={showDisable}
        onClose={() => setShowDisable(false)}
        onDisabled={invalidateStatus}
      />
      <TwoFactorRegenerateCodesModal
        isOpen={showRegenerate}
        onClose={() => setShowRegenerate(false)}
        onRegenerated={invalidateStatus}
      />
    </div>
  )
}
